// LinePlotFilters.js
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import LinePlot from "./LinePlot";
import { resetState, loadFromState, saveToState } from "../utils";
import { getProfiles, getNodes, getProfilerMetadata } from "../api/quontxApi";

const ITEMS_PER_PAGE = 25;

// TEMP: backend returns proxy profile under this uuid (shared across proxies)
// IMPORTANT: we DO NOT send this uuid to any API call.
// We only use it to recognize the returned profile and remap it under the selected proxy uuid.
const PROXY_PROFILE_UUID = "5666258c-3f67-4f16-8167-bff4a1cac760";

const LinePlotFilters = ({
  timestampBuckets = [], // fallback buckets (global)
  domains = [],
  isLoading = false,
  propagnateData,
}) => {
  const maxDomainCount = 5;
  const propagnateFunctionRef = useRef(propagnateData);

  // data keyed by proxy uuid (activeProxy.value)
  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [activeDomains, setActiveDomains] = useState([]);
  const [activeProxy, setActiveProxy] = useState(null);

  const [domainSelectDisabled, setDomainSelectDisabled] = useState(false);

  // proxy-specific buckets fetched on-demand
  const [proxyBuckets, setProxyBuckets] = useState([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);

  const manageActiveDomains = (selectedDomains) => {
    const safe = selectedDomains ?? [];
    if (safe.length <= maxDomainCount) {
      setDomainSelectDisabled(false);
      setActiveDomains(safe.map((d) => ({ label: d.label, value: d.value })));
      return;
    }
    setDomainSelectDisabled(true);
  };

  const resetFilters = () => {
    resetState("linePlotState", [
      { stateFunc: setActiveDomains, nullVal: [] },
      { stateFunc: setActiveProxy, nullVal: null },
    ]);
    setDomainSelectDisabled(false);
    setProxyBuckets([]);
    setData({});
  };

  // ---------------------------
  // Server-side proxy search (type = PROXY)
  // ---------------------------

  const toOption = (node) => ({
    label: node.name,
    value: node.uuid,
  });

  const fetchProxiesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NODE_SET",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const loadProxyOptions = useCallback(
    async (search, loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchProxiesPage({ search: q, page });

      return {
        options: items.map(toOption),
        hasMore: items.length === ITEMS_PER_PAGE,
        additional: { page: page + 1 },
      };
    },
    [fetchProxiesPage]
  );

  const proxyAdditional = useMemo(() => ({ page: 1 }), []);

  // ---------------------------
  // Load timestamp buckets for selected proxy (MUST use proxy uuid)
  // ---------------------------

  const fetchProfilerBucketsForProxy = useCallback(async (proxyId) => {
    if (!proxyId) return [];

    // IMPORTANT: send the proxy uuid here (NOT PROXY_PROFILE_UUID)
    const res = await getProfilerMetadata({
      itemsPerPage: 1,
      entities: [proxyId],
    });

    const rows = res?.data?.data ?? res?.data ?? [];
    const intervals = [];
    rows.forEach((row) => {
      intervals.push(...(row?.metadata?.corpora?.time_intervals ?? []));
    });

    return Array.from(new Set(intervals));
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const proxyId = activeProxy?.value;

      if (!proxyId) {
        setProxyBuckets([]);
        return;
      }

      setLoadingBuckets(true);
      try {
        const buckets = await fetchProfilerBucketsForProxy(proxyId);
        if (!cancelled) setProxyBuckets(buckets);
      } finally {
        if (!cancelled) setLoadingBuckets(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeProxy, fetchProfilerBucketsForProxy]);

  // Use proxy buckets when proxy selected, else fallback buckets
  const availableTimestampBuckets = useMemo(() => {
    return activeProxy?.value ? (proxyBuckets ?? []) : (timestampBuckets ?? []);
  }, [activeProxy, proxyBuckets, timestampBuckets]);

  // ---------------------------
  // Load profile for selected proxy (MUST send proxy uuid)
  // Then remap returned shared profile uuid -> proxy uuid key.
  // ---------------------------

  const loadProfiles = useCallback(async () => {
    const proxyId = activeProxy?.value;
    if (!proxyId) return;

    // IMPORTANT: send the proxy uuid here (NOT PROXY_PROFILE_UUID)
    const profiles = await getProfiles({
      itemsPerPage: 1,
      entities: [proxyId],
    });

    propagnateFunctionRef.current(profiles.data);

    const rows = profiles?.data?.data ?? [];

    // Find the returned profile.
    // Backend currently tags proxy profiles with metadata.corpora.uuid === PROXY_PROFILE_UUID
    // (even though we requested by proxyId).
    const returnedProxyProfileRow =
      rows.find((p) => p?.metadata?.corpora?.uuid === PROXY_PROFILE_UUID) ??
      rows.find((p) => p?.metadata?.corpora?.uuid === proxyId) ??
      rows[0];

    const profile = returnedProxyProfileRow?.values;
    if (!profile) {
      console.warn("[LinePlotFilters] Missing proxy profile in response", {
        requested: proxyId,
        returnedUuids: rows.map((r) => r?.metadata?.corpora?.uuid).filter(Boolean),
      });
      setData({});
      return;
    }

    // Key by selected proxy uuid so LinePlot reads data[activeProxy.value]
    setData({ [proxyId]: profile });
  }, [activeProxy]);

  // ---------------------------
  // localStorage restore/persist
  // ---------------------------

  useEffect(() => {
    loadFromState("linePlotState", [
      {
        stateFunc: setActiveDomains,
        storageObjectKey: "activeDomains",
        defaultVal: [],
      },
      {
        stateFunc: setActiveProxy,
        storageObjectKey: "activeProxy",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  useEffect(() => {
    if (activeDomains.length > 0 || activeProxy !== null) {
      saveToState("linePlotState", { activeDomains, activeProxy });
    }
  }, [activeDomains, activeProxy]);

  // Fetch profile when proxy changes
  useEffect(() => {
    if (!activeProxy?.value) return;

    (async () => {
      setLoadingData(true);
      try {
        await loadProfiles();
      } finally {
        setLoadingData(false);
      }
    })();
  }, [activeProxy, loadProfiles]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}

      {!isLoading && domains && (
        <div className="flex flex-col gap-4 items-center justify-center mb-8">
          <div className="flex gap-4 justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Organization Proxy</p>
              <AsyncPaginate
                value={activeProxy}
                onChange={(selected) => setActiveProxy(selected)}
                loadOptions={loadProxyOptions}
                additional={proxyAdditional}
                debounceTimeout={250}
                cacheOptions
                placeholder="Type to search…"
                noOptionsMessage={({ inputValue }) =>
                  (inputValue || "").trim().length < 2
                    ? "Type at least 2 characters"
                    : "No matches"
                }
              />
              {activeProxy?.value && loadingBuckets && (
                <p className="text-xs opacity-70">Loading buckets…</p>
              )}
            </div>

            <div className="w-[240px] flex flex-col gap-2">
              <p>Societal Domains</p>
              <Select
                isMulti
                isSearchable
                value={activeDomains}
                onChange={(selected) => manageActiveDomains(selected)}
                options={(domains ?? []).map((domain) => ({
                  label: domain.description,
                  value: domain.uuid,
                  isDisabled: domainSelectDisabled,
                }))}
              />
            </div>
          </div>

          <div>
            <button
              onClick={resetFilters}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>

          {activeDomains?.length > 0 &&
            activeProxy?.value &&
            Object.keys(data ?? {}).length > 0 && (
              <div className="flex items-center content-center flex-wrap">
                <LinePlot
                  isLoading={loadingData}
                  data={data}
                  // IMPORTANT: use proxy buckets when available (matches proxy profile indices)
                  timebuckets={availableTimestampBuckets}
                  activeDomains={activeDomains}
                  activeProxy={activeProxy}
                />
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default LinePlotFilters;